import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { DateRangeTypes } from './chartButtons/constants/RangeRoutes';
import { PURGE } from 'redux-persist';

export interface ChartOptions {
  chartId: string;
  filterLegendKeys: string[];
  selectedChartType: string;
  selectedChartRange: string;
}

const defaultChartOptions: ChartOptions = {
  chartId: '',
  filterLegendKeys: [],
  selectedChartType: 'stackedBar',
  selectedChartRange: 'Week'
};

const cachedChartOptions: {
  chartOptionsArray: ChartOptions[];
} = {
  chartOptionsArray: [] as ChartOptions[]
};

const chartOptionsSlice = createSlice({
  name: 'chartFilterKeys',
  initialState: cachedChartOptions,

  reducers: {
    toggleChartLegendFilter: (
      state,
      {
        payload: { chartId, filterLegendKey }
      }: PayloadAction<{ chartId: string; filterLegendKey: string }>
    ) => {
      const chartOptions = state.chartOptionsArray.find(
        (chartOptions: ChartOptions) => chartOptions.chartId === chartId
      );

      if (chartOptions == null) {
        const filterLegendKeys = [filterLegendKey] as string[];

        state.chartOptionsArray.push({
          chartId,
          filterLegendKeys,
          selectedChartType: defaultChartOptions.selectedChartType,
          selectedChartRange: defaultChartOptions.selectedChartRange
        });
      } else {
        if (chartOptions.filterLegendKeys.includes(filterLegendKey)) {
          chartOptions.filterLegendKeys = chartOptions.filterLegendKeys.filter(
            (key: string) => key !== filterLegendKey
          ) as [];
        } else {
          chartOptions.filterLegendKeys.push(filterLegendKey);
        }
      }
    },
    selectChartType: (
      state,
      {
        payload: { chartId, selectedChartType }
      }: PayloadAction<{ chartId: string; selectedChartType: string }>
    ) => {
      const chartToUpdate = state.chartOptionsArray.find(
        (chart: ChartOptions) => chart.chartId === chartId
      );

      if (chartToUpdate != null) {
        chartToUpdate.selectedChartType = selectedChartType;
      } else {
        state.chartOptionsArray.push({
          chartId,
          filterLegendKeys: [],
          selectedChartType,
          selectedChartRange: defaultChartOptions.selectedChartRange
        });
      }
    },
    selectChartRange: (
      state,
      {
        payload: { chartId, selectedChartRange }
      }: PayloadAction<{ chartId: string; selectedChartRange: string }>
    ) => {
      const chartToUpdate = state.chartOptionsArray.find(
        (chart: ChartOptions) => chart.chartId === chartId
      );

      if (chartToUpdate != null) {
        chartToUpdate.selectedChartRange = selectedChartRange;
      } else {
        state.chartOptionsArray.push({
          chartId,
          filterLegendKeys: [],
          selectedChartType: defaultChartOptions.selectedChartType,
          selectedChartRange
        });
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => cachedChartOptions);
  }
});

const selectSelf = (state: RootState) => state.chartOptions;
const selectChartId = (state: RootState, chartId: string) => chartId;
export const getFilteredKeysAsObject = createSelector(
  selectSelf,
  selectChartId,
  (state, chartId) => {
    const chartOptions: any = state.chartOptionsArray.find(
      (chartFilerKeys: ChartOptions) => chartFilerKeys.chartId === chartId
    );

    return {
      filteredLegendKeys: (chartOptions?.filterLegendKeys as string[]) ?? []
    };
  }
);

export const getChartOptions = createSelector(
  selectSelf,
  selectChartId,
  (state, chartId) => {
    const chartOptions = state.chartOptionsArray.find(
      (chartFilerKeys: ChartOptions) => chartFilerKeys.chartId === chartId
    );

    return {
      filteredLegendKeys: (chartOptions?.filterLegendKeys as string[]) ?? [],
      selectedChartType: chartOptions?.selectedChartType as string,
      selectedChartRange: chartOptions?.selectedChartRange as string
    };
  }
);

export const getChartType = createSelector(
  selectSelf,
  selectChartId,
  (state, chartId) => {
    const chartOptions = state.chartOptionsArray.find(
      (chartFilerKeys: ChartOptions) => chartFilerKeys.chartId === chartId
    );

    return chartOptions?.selectedChartType;
  }
);

export const getChartTypeAndRange = createSelector(
  selectSelf,
  selectChartId,
  getChartType,
  (state, chartId, chartType) => {
    const chartOptions = state.chartOptionsArray.find(
      (chartFilerKeys: ChartOptions) => chartFilerKeys.chartId === chartId
    );

    return [
      chartType,
      (chartOptions?.selectedChartRange as DateRangeTypes) ??
        DateRangeTypes.MONTH
    ] as const;
  }
);

const { actions, reducer } = chartOptionsSlice;

export const { toggleChartLegendFilter, selectChartType, selectChartRange } =
  actions;

export default reducer;
